import React from 'react'
import { observer } from 'mobx-react'
import {
  Card,
  CardContent,
  CardFooter,
  CardFooterContent,
  CardFooterDescription,
  CardHeader,
  CardHeaderActions,
  CardTitle,
} from 'components/Card'
import JobViewModel from '../JobViewModel'
import styles from './JobInsightsCard.sass'
import Tooltip from 'components/Tooltip'
import { IconButton } from 'components/IconButton'
import Icon from 'components/Icon'
import { SectionError } from '../Section/Section'
import AnimatedIcon from 'components/AnimatedIcon'

interface JobInsightsCardProps {
  vm: JobViewModel
}

const InsightsLoading = () => {
  return (
    <>
      <CardHeader>
        <CardTitle>Insights</CardTitle>
        <CardHeaderActions>
          <div className={styles.insightsAction}>
            <AnimatedIcon name="stars" />
          </div>
        </CardHeaderActions>
      </CardHeader>
    </>
  )
}

export default observer(function JobInsightsCard({ vm }: JobInsightsCardProps) {
  const periodsOfUnresponsivenessTowardsClient =
    vm.jobInsightsVM.periodsOfUnresponsivenessTowardsClient
  const countOfTotalFinalReturns = vm.jobInsightsVM.countOfTotalFinalReturns
  const countOfTotalDraftReturns = vm.jobInsightsVM.countOfTotalDraftReturns
  const latestInsights = vm.jobInsightsVM.latestInsights
  const insightsLabelText = vm.jobInsightsVM.insightsLabelText
  const isLatestInsightsAvailable = vm.jobInsightsVM.isLatestInsightsAvailable
  const generatedTime = vm.jobInsightsVM.lastGeneratedTimeString
  const regenerateOrGenerateMore = vm.jobInsightsVM.regenerateOrGenerateMore
  const isMoreThanZeroMessagesAvailable =
    vm.jobInsightsVM.isMoreThanZeroMessagesAvailable

  return (
    <Card borderless>
      {vm.jobInsightsVM.activate.match({
        pending: () => <InsightsLoading />,
        rejected: (err) => (
          <>
            <CardHeader>
              <CardTitle>Insights</CardTitle>
            </CardHeader>
            <SectionError
              title="An error occurred while loading the job insights."
              error={err}
            />
          </>
        ),
        resolved: () => (
          <>
            <CardHeader>
              <CardTitle>Insights</CardTitle>
              {insightsLabelText !== null &&
              !isMoreThanZeroMessagesAvailable ? (
                <CardHeaderActions>
                  <div className={styles.insightsAction}>
                    <>
                      {regenerateOrGenerateMore.pending ? (
                        <AnimatedIcon name="stars" />
                      ) : null}
                    </>
                  </div>
                </CardHeaderActions>
              ) : (
                insightsLabelText !== null && (
                  <CardHeaderActions>
                    <IconButton
                      className={styles.insightsAction}
                      onClick={regenerateOrGenerateMore}
                      dataTest="job-insights-regenerate-or-generate-more-button"
                    >
                      {insightsLabelText}
                      <>
                        {regenerateOrGenerateMore.pending ? (
                          <AnimatedIcon name="stars" />
                        ) : (
                          <Icon name="stars" />
                        )}
                      </>
                    </IconButton>
                  </CardHeaderActions>
                )
              )}
            </CardHeader>
            <CardContent
              autoHeight={isLatestInsightsAvailable}
              compactBottomPadding={isLatestInsightsAvailable}
              scroll
            >
              <Tooltip
                content={
                  'This includes periods of unresponsiveness in the chat that lasted longer then 48 hours'
                }
              >
                <div className={styles.text}>
                  There{' '}
                  {periodsOfUnresponsivenessTowardsClient === 1 ? 'is' : 'are'}{' '}
                  a total of{' '}
                  <span className={styles.textBold}>
                    {periodsOfUnresponsivenessTowardsClient}
                  </span>{' '}
                  unresponsive{' '}
                  {periodsOfUnresponsivenessTowardsClient === 1
                    ? 'period'
                    : 'periods'}{' '}
                  towards the Client.
                </div>
              </Tooltip>
              <div className={styles.divider}></div>
              <div className={styles.text}>
                There {countOfTotalDraftReturns === 1 ? 'is' : 'are'}{' '}
                <span className={styles.textBold}>
                  {countOfTotalDraftReturns}
                </span>{' '}
                Draft {countOfTotalDraftReturns === 1 ? 'Return' : 'Returns'}{' '}
                uploaded.
              </div>
              <div className={styles.divider}></div>
              <div className={styles.text}>
                There {countOfTotalFinalReturns === 1 ? 'is' : 'are'}{' '}
                <span className={styles.textBold}>
                  {countOfTotalFinalReturns}
                </span>{' '}
                Final {countOfTotalFinalReturns === 1 ? 'Return' : 'Returns'}{' '}
                uploaded.
              </div>

              {isLatestInsightsAvailable ? (
                latestInsights.map((item, index: number) => (
                  <React.Fragment key={index}>
                    <div className={styles.divider}></div>
                    <div className={styles.text}>
                      {item.prefixContent}{' '}
                      <span className={styles.textBold}>
                        {item.generatedResponse}
                      </span>
                    </div>
                  </React.Fragment>
                ))
              ) : (
                <div className={styles.dividerBottom}></div>
              )}
            </CardContent>
            {isLatestInsightsAvailable && generatedTime !== null && (
              <>
                <CardFooter>
                  <CardFooterContent>
                    Last generated on {generatedTime}
                  </CardFooterContent>
                  {vm.jobInsightsVM.isAnyIrrelevant && (
                    <CardFooterDescription>
                      The Insights above might be stale.
                    </CardFooterDescription>
                  )}
                </CardFooter>
              </>
            )}
          </>
        ),
      })}
    </Card>
  )
})
